import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

type RouteSeo = {
  title: string;
  description: string;
  path: string;
};

const SITE_URL = 'https://innovategames.net';
const SITE_NAME = 'InnoVate Games & Apps';

const routeSeoMap: Record<string, RouteSeo> = {
  '/': {
    title: `${SITE_NAME} | Home`,
    description: 'InnoVate builds polished games and mobile apps from Bahawalpur, Pakistan for players and users worldwide.',
    path: '/',
  },
  '/games': {
    title: `${SITE_NAME} | Games`,
    description: 'Explore InnoVate game projects, gameplay experiences, and upcoming titles across mobile platforms.',
    path: '/games',
  },
  '/apps': {
    title: `${SITE_NAME} | Apps`,
    description: 'Discover utility and lifestyle apps designed and built by the InnoVate team.',
    path: '/apps',
  },
  '/about': {
    title: `${SITE_NAME} | About`,
    description: 'Learn about InnoVate, our team, and our mission to build delightful games and apps.',
    path: '/about',
  },
  '/contact': {
    title: `${SITE_NAME} | Contact`,
    description: 'Contact InnoVate for partnerships, app and game development services, or support.',
    path: '/contact',
  },
  '/privacy-policy': {
    title: `${SITE_NAME} | Privacy Policy`,
    description: 'Read how InnoVate collects, uses, and protects user information in our products and services.',
    path: '/privacy-policy',
  },
  '/terms-of-service': {
    title: `${SITE_NAME} | Terms of Service`,
    description: 'Review the terms and conditions for using InnoVate apps, games, and website services.',
    path: '/terms-of-service',
  },
};

const ensureTag = (selector: string, create: () => HTMLElement): HTMLElement => {
  const existing = document.head.querySelector(selector);
  if (existing) {
    return existing as HTMLElement;
  }

  const tag = create();
  document.head.appendChild(tag);
  return tag;
};

const SeoManager = () => {
  const location = useLocation();

  useEffect(() => {
    const normalizedPath = location.pathname.toLowerCase().replace(/\/$/, '') || '/';
    const routeSeo = routeSeoMap[normalizedPath] ?? {
      title: `${SITE_NAME} | Page Not Found`,
      description: 'The page you requested could not be found on InnoVate.',
      path: '/404',
    };

    document.title = routeSeo.title;

    const description = ensureTag('meta[name="description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      return meta;
    });
    description.setAttribute('content', routeSeo.description);

    const canonical = ensureTag('link[rel="canonical"]', () => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      return link;
    });
    canonical.setAttribute('href', `${SITE_URL}${routeSeo.path}`);

    const ogTitle = ensureTag('meta[property="og:title"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      return meta;
    });
    ogTitle.setAttribute('content', routeSeo.title);

    const ogDescription = ensureTag('meta[property="og:description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      return meta;
    });
    ogDescription.setAttribute('content', routeSeo.description);

    const ogUrl = ensureTag('meta[property="og:url"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:url');
      return meta;
    });
    ogUrl.setAttribute('content', `${SITE_URL}${routeSeo.path}`);
  }, [location.pathname]);

  return null;
};

export default SeoManager;
